import { useNavigate } from "react-router-dom";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, AlertTriangle, Package, Gift } from "lucide-react";
import { formatPrice, validateSamplePackQuantity } from "@/lib/cart";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { useCheckoutSettings, useProducts, useSamplePackSettings } from "@/lib/firestoreService";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

export const CartSheet = ({ open, onOpenChange, items, total, updateQty, removeItem, onAuthOpen }) => {
  const { t } = useLang();
  const { firebaseReady, user } = useAuth();
  const navigate = useNavigate();
  const settings = useCheckoutSettings();
  const products = useProducts();
  const samplePackSettings = useSamplePackSettings();

  // Re-check stock against live product data — an item can go out of stock
  // after it was added to the cart (cart items are a frozen snapshot).
  const soldOutKeys = new Set(
    items.filter(it => products?.find(p => p.id === it.product_id)?.in_stock === false).map(it => it.key)
  );
  const hasSoldOutItems = soldOutKeys.size > 0;

  const mustSignIn = !settings.enableGuestCheckout && !user;
  const canCheckout = firebaseReady && items.length > 0 && !mustSignIn && !hasSoldOutItems;

  const grandTotal = total;

  const handleCheckout = () => {
    track("checkout_initiated", {
      item_count: items.length,
      cart_total: grandTotal,
    });
    onOpenChange(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="cart-sheet"
        className="bg-[#F5E6CC] border-l border-[#5A2D1D]/20 w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="p-6 border-b border-[#5A2D1D]/15">
          <SheetTitle className="font-display text-2xl text-[#5A2D1D] flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> {t.cart.title}
          </SheetTitle>
          <SheetDescription className="text-[#2E1A12]/70">{t.cart.sub}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="cart-items">
          {items.length === 0 && (
            <div data-testid="empty-cart-message" className="text-center py-16 text-[#2E1A12]/70">
              <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-[#5A2D1D]/40" />
              <p className="font-semibold text-[#2E1A12]/80">{t.cart.empty}</p>
              <p className="text-sm mt-1">{t.cart.empty_sub}</p>
              <a
                href="/products"
                onClick={() => onOpenChange(false)}
                className="mt-5 inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-5 py-2.5 rounded-full font-bold text-sm transition-colors"
              >
                View Products <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}

          {items.map((it) => {
            const soldOut = soldOutKeys.has(it.key);
            return (
            <div
              key={it.key}
              data-testid={`cart-item-${it.key}`}
              className={`flex gap-4 bg-[#F5E6CC] border rounded-sm p-3 ${soldOut ? "border-[#7A3B25]/50" : "border-[#5A2D1D]/20"}`}
            >
              {it.image_url ? (
                <img
                  src={it.image_url}
                  alt={it.name}
                  loading="lazy"
                  decoding="async"
                  className={`w-20 h-20 object-cover rounded-sm flex-shrink-0 ${soldOut ? "grayscale opacity-60" : ""}`}
                />
              ) : (
                <div className="w-20 h-20 rounded-sm flex-shrink-0 bg-[#556B2F]/10 text-[#556B2F] grid place-items-center">
                  {it.item_type === "sample_pack" ? <Gift className="w-7 h-7" /> : <Package className="w-7 h-7" />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-[#5A2D1D] truncate">{it.name}</div>
                <div className="text-xs text-[#556B2F] uppercase tracking-widest font-bold mt-0.5">{it.size}</div>
                {soldOut && (
                  <div className="text-xs text-[#7A3B25] font-bold mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Sold out — please remove
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between">
                  <div className="inline-flex items-center border border-[#5A2D1D]/30 rounded-sm">
                    <button
                      data-testid={`decrease-${it.key}`}
                      onClick={() => updateQty(it.key, it.quantity - 1)}
                      className="px-2 py-1 text-[#5A2D1D] hover:bg-[#5A2D1D]/10"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span data-testid={`qty-${it.key}`} className="px-3 text-sm font-semibold min-w-[26px] text-center">
                      {it.quantity}
                    </span>
                    <button
                      data-testid={`increase-${it.key}`}
                      onClick={() => {
                        if (it.item_type === "sample_pack") {
                          const maxQty = samplePackSettings.packs[it.pack_type]?.maxQtyPerOrder ?? 3;
                          if (!validateSamplePackQuantity(items, it.pack_type, 1, maxQty)) {
                            toast.error(`Maximum ${maxQty} ${it.name} per order`);
                            return;
                          }
                        }
                        updateQty(it.key, it.quantity + 1);
                      }}
                      className="px-2 py-1 text-[#5A2D1D] hover:bg-[#5A2D1D]/10"
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="font-bold text-[#5A2D1D]">
                    {formatPrice(it.price * it.quantity)}
                  </div>
                </div>
              </div>
              <button
                data-testid={`remove-${it.key}`}
                onClick={() => { removeItem(it.key); track("product_removed_from_cart", { product_id: it.product_id, product_name: it.name, size: it.size }); }}
                className="text-[#5A2D1D]/60 hover:text-[#7A3B25] self-start"
                aria-label="Remove item"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            );
          })}
        </div>

        <div className="border-t border-[#5A2D1D]/15 p-6 bg-[#EFD9B4]/60">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm uppercase tracking-widest font-bold text-[#556B2F]">
              {t.cart.total}
            </span>
            <span data-testid="cart-total" className="font-display text-2xl font-bold text-[#5A2D1D]">
              {formatPrice(grandTotal)}
            </span>
          </div>

          {mustSignIn && items.length > 0 ? (
            <div className="space-y-3">
              <div className="text-center text-sm text-[#5A2D1D] font-semibold">
                Sign in to place your order
              </div>
              <button
                onClick={() => { onOpenChange(false); onAuthOpen?.(); }}
                className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full font-bold text-lg bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] transition-colors shadow-lg"
              >
                Sign In to Checkout
                <ArrowRight className="w-5 h-5" />
              </button>
              <p className="text-xs text-[#2E1A12]/50 text-center">Account required to track your order</p>
            </div>
          ) : (
            <>
              {hasSoldOutItems && (
                <p className="text-xs text-[#7A3B25] font-bold text-center mb-2 flex items-center justify-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Remove sold-out items to continue
                </p>
              )}
              <button
                data-testid="whatsapp-checkout-button"
                disabled={!canCheckout}
                onClick={handleCheckout}
                className={`w-full inline-flex items-center justify-center gap-2 py-4 rounded-full font-bold text-lg transition-colors ${
                  !canCheckout
                    ? "bg-[#5A2D1D]/30 text-[#F5E6CC]/70 cursor-not-allowed"
                    : "bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] shadow-lg"
                }`}
              >
                Proceed to Checkout
                <ArrowRight className="w-5 h-5" />
              </button>
              <p className="text-xs text-[#2E1A12]/60 text-center mt-3">{t.cart.note}</p>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
